import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import {
    PaletteMode,
    PaletteOptions,
    ThemeOptions,
    alpha,
    darken,
    lighten,
} from '@mui/material';
import { PRIMARY_COLOR, SECONDARY_COLOR } from './colors';
const lightThemeTokens: PaletteOptions = {
    primary: {
        light: '#4dc0ce',
        main: PRIMARY_COLOR,
        dark: '#007382',
        contrastText: '#01444d',
    },
    secondary: {
        light: '#f6ad33',
        main: SECONDARY_COLOR,
        dark: '#c37a00',
        darker: darken('#c37a00', 0.35),
        contrastText: '#000000',
    },
    text: {
        primary: '#2c2c2c',
        secondary: '#383838',
    },
    background: {
        paper: '#ffffff',
        default: '#ffffff',
    },
    action: {
        active: '#444444',
        hover: alpha(PRIMARY_COLOR, 0.05),
        selected: alpha(PRIMARY_COLOR, 0.1),
        disabled: '#666666',
    },
};

const darkThemeTokens: PaletteOptions = {
    primary: {
        light: '#4dc0ce',
        main: PRIMARY_COLOR,
        dark: '#007382',
        contrastText: '#01444d',
    },
    secondary: {
        light: '#f6ad33',
        main: SECONDARY_COLOR,
        dark: '#c37a00',
        darker: darken('#c37a00', 0.35),
        contrastText: '#ffffff',
    },
    background: {
        paper: '#1c262d',
        default: '#1c262d',
    },
    text: {
        primary: '#fff',
        secondary: '#bbb',
    },
    divider: '#333C42',
    action: {
        hover: alpha(PRIMARY_COLOR, 0.05),
        selected: alpha(PRIMARY_COLOR, 0.1),
        disabled: '#666666',
    },
};

export const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    workflowTitle: 'h2',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    borderBottom: 'none',
                    boxShadow: 'none',
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                enterDelay: 500,
                enterNextDelay: 500,
            },
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    maxWidth: 320,
                    backgroundColor: alpha(
                        lighten(theme.palette.background.default, 0.1),
                        0.95
                    ),
                }),
                popper: {
                    zIndex: 1600, // Higher than Modal's 1501
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    fontSize: 12,
                }),

                head: ({ theme }) => ({
                    backgroundColor: darken(
                        theme.palette.background.default,
                        0.1
                    ),
                }),
            },
        },

        MuiInput: {
            styleOverrides: {
                input: {
                    paddingTop: 0,
                },
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiOutlinedInput-root': {
                        transition: theme.transitions.create(
                            ['border-color', 'background-color'],
                            {
                                duration: theme.transitions.duration.short,
                            }
                        ),
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        paddingRight: 16,
                        paddingLeft: 8,
                        fontSize: 14,
                        borderColor: theme.palette.divider,
                        transition: theme.transitions.create('border-color', {
                            duration: theme.transitions.duration.short,
                        }),
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                        {
                            borderColor: theme.palette.primary.main,
                        },
                    '& .MuiOutlinedInput-root:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                    '& .MuiInputBase-input': {
                        paddingRight: 8,
                        paddingLeft: 8,
                        fontSize: 14,
                        paddingTop: 6,
                        paddingBottom: 6,
                    },
                    '& .MuiInputBase-inputMultiline': {
                        paddingTop: 0,
                    },
                    '& .MuiInputBase-root.MuiInputBase-multiline': {
                        paddingRight: 0,
                        paddingLeft: 2,
                        fontSize: 14,
                        paddingTop: 10,
                        paddingBottom: 4,
                        lineHeight: 1.2,
                    },
                }),
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                    fontSize: 14,
                    top: -2,
                    left: 0,
                    '&[data-shrink="false"]': {
                        left: -4,
                        top: -3,
                    },
                }),
            },
        },

        MuiSelect: {
            styleOverrides: {
                root: ({ theme }) => ({
                    zIndex: 1900,
                    fontSize: 14,
                    '&.MuiOutlinedInput-root': {
                        transition: theme.transitions.create(
                            ['border-color', 'background-color'],
                            {
                                duration: theme.transitions.duration.short,
                            }
                        ),
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.divider,
                            transition: theme.transitions.create(
                                'border-color',
                                {
                                    duration: theme.transitions.duration.short,
                                }
                            ),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                        },
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                        },
                    },
                }),
                select: {
                    paddingTop: 6,
                    paddingBottom: 5,
                    paddingLeft: '8px !important',
                    paddingRight: 8,
                    fontSize: 14,
                },
            },
            defaultProps: {
                MenuProps: {
                    sx: {
                        '& .MuiMenu-paper': {
                            marginTop: '4px',
                        },
                        '& .MuiMenuItem-root': {
                            paddingLeft: '8px !important',
                        },
                    },
                },
            },
        },

        MuiAutocomplete: {
            styleOverrides: {
                popper: {
                    zIndex: 1600,
                    '& .MuiPaper-root': {
                        marginTop: '4px',
                    },
                },
                listbox: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                inputRoot: {
                    '&.MuiOutlinedInput-root': {
                        padding: 0,
                        fontSize: 14,
                        '& .MuiAutocomplete-input': {
                            paddingTop: 6,
                            paddingBottom: 6,
                            paddingLeft: 8,
                            paddingRight: 8,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            paddingRight: 16,
                            paddingLeft: 8,
                        },
                    },
                    '&.MuiInputBase-sizeSmall': {
                        '& .MuiAutocomplete-input': {
                            paddingTop: 6,
                            paddingBottom: 6,
                        },
                    },
                },
                endAdornment: {
                    '& .MuiAutocomplete-popupIndicator': {
                        padding: 2,
                        '& .MuiSvgIcon-root': {
                            fontSize: 20,
                        },
                    },
                },
            },
        },

        MuiMenu: {
            defaultProps: {
                elevation: 1,
            },
            styleOverrides: {
                paper: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    marginTop: theme.spacing(0.5),
                    padding: theme.spacing(0.5),
                    border: `1px solid ${theme.palette.divider}`,
                    '& .MuiList-root': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(0.5),
                    },
                }),
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    fontSize: 12,
                    fontWeight: 400,
                    minHeight: 28,
                    borderRadius: theme.shape.borderRadius,
                    display: 'flex',
                    gap: theme.spacing(1),
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                    '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        },
                    },
                    '& .MuiListItemIcon-root': {
                        minWidth: '16px',
                    },
                    '& .MuiListItemText-root': {
                        margin: 0,
                    },
                    '& .MuiTypography-root': {
                        fontSize: 12,
                    },
                }),
            },
        },

        MuiModal: {
            styleOverrides: {
                root: {
                    zIndex: 1501, // Higher than PopperPanel's 1400
                },
            },
        },

        MuiToolbar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    minHeight: 56,
                    paddingLeft: theme.spacing(0),
                    paddingRight: theme.spacing(0),
                    [theme.breakpoints.up('sm')]: {
                        paddingLeft: theme.spacing(0),
                        paddingRight: theme.spacing(0),
                    },
                }),
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    padding: 0,
                    '@media (pointer: coarse)': {
                        padding: 0,
                    },
                },
                rail: {
                    borderRadius: 4,
                },
                track: {
                    height: 3,
                    borderRadius: 4,
                },
                thumb: ({ theme }) => ({
                    height: 12,
                    width: 12,
                    background: theme.palette.background.default,
                    border: `2px solid ${theme.palette.primary.main}`,
                    '&:hover, &.Mui-active, &.Mui-focusVisible': {
                        boxShadow: 'inherit',
                    },
                }),
            },
        },
        MuiCollapse: {
            styleOverrides: {
                root: {
                    '.MuiTreeItem-group': {
                        marginLeft: 8,
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: ({ theme }) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                    padding: theme.spacing(0.25),
                    gap: theme.spacing(0.25),
                }),
            },
        },

        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    color: theme.palette.text.secondary,
                    border: 'none',
                    textTransform: 'none',
                    backgroundColor: 'transparent',
                    '&.Mui-disabled': {
                        color: theme.palette.action.disabled,
                        border: 'none',
                    },
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        border: 'none',
                    },
                    '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                        color: theme.palette.text.primary,
                        border: 'none',
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                            border: 'none',
                        },
                    },
                }),

                sizeSmall: {
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    height: 'auto',
                },
                sizeMedium: {
                    padding: '4px 8px',
                    height: '36px',
                },
            },
        },

        MuiList: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: 0,
                    paddingBottom: 0,
                    '& li': {
                        '&::marker': {
                            color: theme.palette.primary.main,
                        },
                    },
                }),
            },
        },

        MuiBreadcrumbs: {
            styleOverrides: {
                ol: {
                    alignItems: 'center',
                    gap: 0,
                },
                li: {
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                },
                separator: ({ theme }) => ({
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5),
                }),
            },
        },

        MuiCssBaseline: {
            styleOverrides: {
                '::selection': {
                    backgroundColor: PRIMARY_COLOR,
                    color: '#ffffff',
                },
                '::-webkit-selection': {
                    backgroundColor: PRIMARY_COLOR,
                    color: '#ffffff',
                },
                body: {
                    WebkitFontSmoothing: 'subpixel-antialiased',
                    MozOsxFontSmoothing: 'subpixel-antialiased',
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        backgroundColor: 'transparent',
                        width: 5,
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb':
                        {
                            backgroundColor: '#6b6b6b',
                            minHeight: 36,
                            width: 1,
                        },
                    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
                        {
                            backgroundColor: '#959595',
                        },
                    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
                        {
                            backgroundColor: '#959595',
                        },
                    '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
                        {
                            backgroundColor: '#959595',
                        },
                    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner':
                        {
                            backgroundColor: '#2b2b2b',
                        },
                },
            },
        },
        MuiAccordion: {
            defaultProps: {
                disableGutters: true,
                square: true,
                elevation: 0,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&::before': {
                        display: 'none',
                    },
                }),
            },
        },
        MuiAccordionSummary: {
            defaultProps: {
                expandIcon: <ArrowForwardIosSharpIcon sx={{ fontSize: 14 }} />,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    border: 0,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, .05)'
                            : 'rgba(0, 0, 0, .03)',
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                        transform: 'rotate(90deg)',
                    },
                    '& .MuiAccordionSummary-content': {
                        marginLeft: theme.spacing(2),
                    },
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                }),
                sizeSmall: {
                    fontSize: '10px',
                    height: '20px',
                    padding: '0 4px',
                },
            },
        },

        MuiCheckbox: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                sizeMedium: {
                    padding: '6px',
                    '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        width: '18px',
                        height: '18px',
                    },
                },
                sizeSmall: {
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        width: '14px',
                        height: '14px',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    transition: theme.transitions.create(
                        ['border-color', 'background-color'],
                        {
                            duration: theme.transitions.duration.short,
                        }
                    ),
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        color: theme.palette.action.disabled,
                    },
                }),
            },
            variants: [
                {
                    props: { selected: true },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        },
                    }),
                },
            ],
        },
        MuiButton: {
            styleOverrides: {
                sizeSmall: {
                    padding: '4px',
                    fontSize: '0.75rem',
                    height: 'auto',
                },
                root: {
                    textTransform: 'none',
                    fontSize: 14,
                    paddingTop: 6,
                    paddingBottom: 6,
                    lineHeight: 1.2,
                },
                contained: ({ theme }) => ({
                    color: '#ffffff',
                    '&.MuiButton-containedPrimary': {
                        color: '#ffffff',
                    },
                    '&.Mui-disabled': {
                        color: theme.palette.text.disabled,
                    },
                }),
                outlined: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 24,
                    height: 14,
                    padding: 0,
                    display: 'flex',
                    '&:active': {
                        '& .MuiSwitch-thumb': {
                            width: 13,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            transform: 'translateX(7px)',
                        },
                    },
                },
                switchBase: () => ({
                    padding: 2,
                    '&.Mui-checked': {
                        transform: 'translateX(10px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                            opacity: 1,
                            backgroundColor: 'primary.main',
                        },
                    },
                }),
                thumb: ({ theme }) => ({
                    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    transition: theme.transitions.create(['width'], {
                        duration: 200,
                    }),
                }),
                track: ({ theme }) => ({
                    borderRadius: 14 / 2,
                    opacity: 1,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,.35)'
                            : 'rgba(0,0,0,.25)',
                    boxSizing: 'border-box',
                }),
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                }),
            },
        },
    },
    typography: (theme) => ({
        h6: {
            fontSize: 19,
        },
        body2: {
            fontSize: 14,
            color: theme.text.secondary,
        },
        workflowTitle: {
            fontSize: 14,
            fontWeight: 500,
        },
        fontFamily: [
            'Poppins',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
    }),
    palette: {
        mode,
        ...(mode === 'light' ? lightThemeTokens : darkThemeTokens),
    },
    shape: {
        borderRadius: 4,
    },
    mixins: {
        glassOverlay: {
            background: 'rgba(4, 20, 32, 0.7)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
        },
    },
});

declare module '@mui/material/styles' {
    interface PaletteColor {
        darker?: string;
    }

    interface SimplePaletteColorOptions {
        darker?: string;
    }

    interface Mixins {
        glassOverlay: import('react').CSSProperties;
    }

    interface MixinsOptions {
        glassOverlay?: import('react').CSSProperties;
    }

    interface TypographyVariants {
        workflowTitle: import('react').CSSProperties;
    }

    interface TypographyVariantsOptions {
        workflowTitle?: import('react').CSSProperties;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonOwnProps {
        selected?: boolean;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        workflowTitle: true;
    }
}
